/**
 * @file ChatWindow.tsx
 * @description Рамка окна «Сообщения» (chat-window-design.md §1б–д, §6;
 * SC64): компактное окно 720×840 справа внизу от 1024 (немодальное —
 * страница под ним работает), развёрнутое на весь экран под полосой входа
 * под сотрудником (две зоны до 1280, три от 1280), на планшете сразу весь
 * экран в две зоны, на телефоне весь экран: список → диалог → назад.
 *
 * Слой `--z-chat` (45): выше нижних полос, шапки и меню, ниже затемнения,
 * окон, выпадающих списков и всплывающих сообщений. Esc — развёрнутое →
 * компактное → кнопка; Alt+↑ / Alt+↓ — соседний диалог.
 */

import { Maximize2, Minimize2, Minus } from "lucide-react";
import { useEffect, useRef, type KeyboardEvent } from "react";

import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { customerName } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";
import { useChatWindow } from "./chat-window-context";
import { ChatWindowEmpty } from "./ChatWindowEmpty";
import { ConversationArea } from "./ConversationArea";
import { ConversationList } from "./ConversationList";
import { useConversationList } from "./useConversationList";
import { useFocusTrap } from "./useFocusTrap";

/** Высота компактного окна: до 840; меньше 560 — заходит на шапку (16 от верха). */
const COMPACT_HEIGHT =
  "max(min(840px, calc(100dvh - var(--shell-top,0px) - 104px)), min(560px, calc(100dvh - var(--shell-top,0px) - 40px)))";

export function ChatWindow() {
  const chat = useChatWindow();
  const { state, presentation, viewport } = chat;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const phone = viewport === "phone";
  const tab = chat.canDispatch ? state.tab : "mine";
  const list = useConversationList(
    chat.enabled && state.open,
    { tab, unassigned: chat.canDispatch && state.unassigned, customerId: state.customer?.id ?? null, search: state.search },
    chat.dataVersion,
  );
  useFocusTrap(rootRef, state.open && presentation.modal);

  // Фокус при открытии: без выбранного диалога — на вкладку (поле ввода берёт фокус само).
  useEffect(() => {
    if (!state.open || state.selectedId) return;
    const target = rootRef.current?.querySelector<HTMLElement>("[role='tab'][aria-selected='true'], input[type='search']");
    target?.focus();
  }, [state.open, state.selectedId]);

  if (!chat.enabled || !state.open) return null;

  const selected = list.items.find((c) => c.id === state.selectedId) ?? null;
  const compact = presentation.frame === "compact";
  const listWidth = compact ? (viewport === "wide" ? "w-[296px]" : "w-[260px]") : viewport === "tablet" ? "w-[300px]" : "w-80 min-[1600px]:w-90";
  const showList = !phone || !state.selectedId;
  const showConversation = !phone || state.selectedId !== null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // События из окон и меню в портале (они в дереве React, но не в DOM окна)
    // — их Esc закрывает только их самих.
    if (!rootRef.current?.contains(event.target as Node)) return;
    if (event.key === "Escape" && !event.defaultPrevented) {
      event.preventDefault();
      if (state.expanded && presentation.canExpand) chat.setExpanded(false);
      else if (phone && state.selectedId) chat.back();
      else chat.close();
      return;
    }
    if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      const index = list.items.findIndex((c) => c.id === state.selectedId);
      const next = list.items[event.key === "ArrowDown" ? index + 1 : Math.max(0, index - 1)];
      if (next) {
        event.preventDefault();
        chat.select(next.id);
      }
    }
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal={presentation.modal}
      aria-label={CHAT_TEXT.windowTitle}
      onKeyDown={onKeyDown}
      className={cn(
        "fixed z-[var(--z-chat)] flex flex-col overflow-hidden bg-surface-lowest",
        compact
          ? "bottom-6 right-6 w-[720px] rounded-2xl [box-shadow:0_24px_48px_var(--shadow-ambient),inset_0_0_0_1px_var(--outline-variant)]"
          : "inset-x-0 bottom-0 top-[var(--shell-top,0px)]",
      )}
      style={compact ? { height: COMPACT_HEIGHT } : undefined}
    >
      {showList || !phone ? (
        <div className={cn("flex h-14 shrink-0 items-center gap-1 border-b border-outline-variant pl-4 pr-1", phone && state.selectedId && "hidden")}>
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-on-surface">{CHAT_TEXT.windowTitle}</h2>
          {presentation.canExpand ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={state.expanded ? CHAT_TEXT.shrink : CHAT_TEXT.expand}
              title={state.expanded ? CHAT_TEXT.shrink : CHAT_TEXT.expand}
              onClick={() => chat.setExpanded(!state.expanded)}
            >
              {state.expanded ? <Minimize2 className="size-4" aria-hidden /> : <Maximize2 className="size-4" aria-hidden />}
            </Button>
          ) : null}
          <Button variant="ghost" size="icon-sm" className="max-lg:size-12" aria-label={CHAT_TEXT.collapse} title={CHAT_TEXT.collapse} onClick={chat.close}>
            <Minus className="size-4" aria-hidden />
          </Button>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {showList ? (
          <div className={cn("flex min-h-0 flex-col", phone ? "w-full" : `${listWidth} shrink-0 border-r border-outline-variant`)}>
            <ConversationList list={list} />
          </div>
        ) : null}
        {showConversation ? (
          state.selectedId ? (
            <ConversationArea key={state.selectedId} id={state.selectedId} fallbackName={selected ? customerName(selected.customer) : ""} />
          ) : (
            // Число — по серверной сводке, а не по загруженной странице списка.
            <ChatWindowEmpty unread={chat.unread} />
          )
        ) : null}
      </div>
    </div>
  );
}
